import { computed } from "vue";
import { EquipmentConfiguration, EquipmentConfigurationOption, EquipmentType, Task } from "@/types/v2";
import { useTask } from "@/composables/tasks/useTask";
import { v4 as uuidv4 } from "uuid";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";

export function useEquipment() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = useTask();

  // ===================================================================
  // Composables
  // ===================================================================
  const { TASK_OBJECTIVES } = useTask();
  const taskHistory = useTaskHistory();

  // ===================================================================
  // Types
  // ===================================================================
  type EligibleTaskObjective = typeof ELIGIBLE_TASK_OBJECTIVES[number];

  // ===================================================================
  // Constants
  // ===================================================================
  const ELIGIBLE_TASK_OBJECTIVES = [
    TASK_OBJECTIVES.VACCINATION,
    TASK_OBJECTIVES.CHALLENGE,
  ];

  // ===================================================================
  // Guards
  // ===================================================================
  const isTaskWithEquipment = (task: Task | undefined | null): task is Task & {
    details: { equipmentConfigurations: EquipmentConfiguration[], equipmentTypes: EquipmentType[] }
  } => {
    if (!task) return false;

    const isEligible = ELIGIBLE_TASK_OBJECTIVES.includes(task.objective.id as EligibleTaskObjective);

    return isEligible &&
      'details' in task &&
      task.details &&
      'equipmentConfigurations' in task.details;
  };

  // ===================================================================
  // Computed
  // ===================================================================
  // Get equipment configurations from active task
  const getEquipmentConfigurations = computed((): EquipmentConfiguration[] => {
    if (activeTask.value && isTaskWithEquipment(activeTask.value)) {
      return activeTask.value.details.equipmentConfigurations;
    }
    return [];
  });

  // Get all available equipment types
  const getEquipmentTypes = computed((): EquipmentType[] => {
    if (activeTask.value && isTaskWithEquipment(activeTask.value)) {
      return activeTask.value.details.equipmentTypes || [];
    }
    return [];
  });

  // Get already configured equipment type IDs
  const getConfiguredEquipmentTypeIds = computed((): (string | number)[] => {
    return getEquipmentConfigurations.value.map(config => config.equipmentType.id);
  });

  // Get available (not yet configured) equipment types
  const getAvailableEquipmentTypes = computed((): EquipmentType[] => {
    return getEquipmentTypes.value.filter(type =>
      !getConfiguredEquipmentTypeIds.value.includes(type.id)
    );
  });

  // Get equipment-related history entries
  const getEquipmentHistory = computed(() => {
    const history = taskHistory.getHistory();
    return history.filter(entry =>
      entry.contextType === 'equipment' ||
      entry.fieldPath.startsWith('equipment.') ||
      entry.context?.toLowerCase().includes('equipment')
    ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });

  // ===================================================================
  // Helpers
  // ===================================================================
  // Format value based on type
  const formatValue = (value: any, type: string): string => {
    if (value === null || value === undefined) return "-";

    if (type === "boolean") {
      return value ? "Yes" : "No";
    } else if (type === "number") {
      return value.toString();
    } else {
      return value;
    }
  };

  // Get option label from config options
  const getOptionLabel = (options: EquipmentConfigurationOption[], name: string): string => {
    if (!options || !Array.isArray(options)) return formatFieldName(name);

    const option = options.find(opt => opt.name === name);
    return option ? (option.label || formatFieldName(name)) : formatFieldName(name);
  };

  // Format field name for display
  const formatFieldName = (name: string): string => {
    if (!name) return "";
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  // Check if a configuration option is modifiable
  const isOptionModifiable = (option: EquipmentConfigurationOption): boolean => {
    return option.modifiable_in_app !== false;
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Get initial values for a new configuration
  const getInitialValues = (equipmentType: EquipmentType): Record<string, any> => {
    const values: Record<string, any> = {};

    equipmentType.configOptions.forEach((option) => {
      // Set default values based on option type
      if (option.type === 'boolean') {
        values[option.name] = false;
      } else if (option.type === 'number') {
        values[option.name] = null;
      } else if (option.type === 'select' && option.options && option.options.length > 0) {
        values[option.name] = option.options[0];
      } else {
        values[option.name] = '';
      }
    });

    return values;
  };

  const hasWeightIdRequirement = (): boolean => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) {
      return false;
    }

    const weightEquipment = activeTask.value.details.equipmentConfigurations.find(config => {
      return config.equipmentType.name === "Weight" &&
        config.values &&
        'weightIdRequired' in config.values &&
        config.values.weightIdRequired === true;
    });

    return !!weightEquipment;
  };

  // Get Weight equipment configuration
  const getWeightEquipmentConfig = (): EquipmentConfiguration | undefined => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) {
      return undefined;
    }

    return activeTask.value.details.equipmentConfigurations.find(config => {
      return config.equipmentType.name === "Weight";
    });
  };

  // Get current weightId value (initialize to empty string if not exists)
  const getWeightId = (): string => {
    const weightConfig = getWeightEquipmentConfig();
    if (!weightConfig || !weightConfig.values) {
      return '';
    }
    
    // Initialize weightId if it doesn't exist
    if (!('weightId' in weightConfig.values)) {
      weightConfig.values.weightId = '';
    }
    
    return weightConfig.values.weightId as string || '';
  };

  const isWeightIdConfigured = (): boolean => {
    if (!hasWeightIdRequirement()) return true;
    return getWeightId().trim().length > 0;
  };

  const hasMissingWeightIdsInRecords = (): boolean => {
    if (!hasWeightIdRequirement() || !activeTask.value) return false;

    const records = (activeTask.value as any)?.details?.weightConfiguration?.progressData;
    if (!Array.isArray(records) || records.length === 0) return false;

    return records.some((record: { weightId?: string | null }) => {
      return !record?.weightId || record.weightId.trim().length === 0;
    });
  };

  const getWeightIdCompletionValidation = (): { isValid: boolean; reason: string | null } => {
    if (!hasWeightIdRequirement()) {
      return { isValid: true, reason: null };
    }

    if (!isWeightIdConfigured()) {
      return {
        isValid: false,
        reason: "Weight ID is required. Set it in Equipment before finishing this task."
      };
    }

    if (hasMissingWeightIdsInRecords()) {
      return {
        isValid: false,
        reason: "Some recorded weights are missing a Weight ID. Set the Weight ID in Equipment to apply it to all records."
      };
    }

    return { isValid: true, reason: null };
  };

  // ===================================================================
  // Actions
  // ===================================================================
  // Create equipment in configuration
  const createEquipmentInConfiguration = (configuration: EquipmentConfiguration): void => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) return;
    activeTask.value.details.equipmentConfigurations.push(configuration);
  };

  // Update equipment configuration
  const updateEquipmentConfiguration = (updatedConfig: EquipmentConfiguration): void => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) return;

    const configIndex = activeTask.value.details.equipmentConfigurations.findIndex(
      config => config.id === updatedConfig.id
    );

    if (configIndex !== -1) {
      activeTask.value.details.equipmentConfigurations[configIndex] = {
        ...activeTask.value.details.equipmentConfigurations[configIndex],
        values: { ...updatedConfig.values },
        updated_at: new Date().toISOString()
      };
    }
  };

  // Remove equipment configuration
  const removeEquipmentConfiguration = async (config: EquipmentConfiguration | string | number): Promise<boolean> => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) return false;

    const configId = typeof config === 'object' ? config.id : config;
    const configToRemove = typeof config === 'object'
      ? config
      : activeTask.value.details.equipmentConfigurations.find(c => c.id === configId);

    if (!configToRemove) return false;

    try {
      const success = await taskHistory.recordChange({
        fieldPath: `equipment.remove`,
        fieldName: 'Remove Equipment Configuration',
        oldValue: configToRemove.equipmentType.name,
        newValue: null,
        context: `Equipment: ${configToRemove.equipmentType.name}`,
        contextType: 'equipment',
        workflowStage: 'configuration',
        requireJustification: true
      });

      if (success) {
        activeTask.value.details.equipmentConfigurations =
          activeTask.value.details.equipmentConfigurations.filter(
            config => config.id !== configId
          );
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error removing equipment configuration:", error);
      return false;
    }
  };

  // Create a new equipment configuration
  const createNewConfiguration = async (equipmentTypeId: string | number): Promise<void> => {
    if (!activeTask.value || !isTaskWithEquipment(activeTask.value)) return;

    const equipmentType = getEquipmentTypes.value.find(type => type.id === equipmentTypeId);
    if (!equipmentType) return;

    const values = getInitialValues(equipmentType);

    const newConfig: EquipmentConfiguration = {
      id: uuidv4(),
      equipmentType: equipmentType,
      values: values,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    await taskHistory.recordChange({
      fieldPath: `equipment.add`,
      fieldName: 'Add Equipment Configuration',
      oldValue: null,
      newValue: equipmentType.name,
      context: `Equipment: ${equipmentType.name}`,
      contextType: 'equipment',
      workflowStage: 'configuration',
      requireJustification: true,
    });

    createEquipmentInConfiguration(newConfig);
  };

  const updateConfigValue = async (
    config: EquipmentConfiguration,
    key: string,
    newValue: any
  ): Promise<void> => {
    const oldValue = config.values[key];

    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
      return;
    }

    const optionDef = config.equipmentType.configOptions.find(opt => opt.name === key);
    const fieldLabel = optionDef ? (optionDef.label || formatFieldName(key)) : formatFieldName(key);
    const isWeightIdField = config.equipmentType.name === "Weight" && key === "weightId";
    const oldWeightId = String(oldValue ?? "").trim();
    const newWeightId = String(newValue ?? "").trim();
    const isFirstWeightIdEntry = isWeightIdField && oldWeightId.length === 0 && newWeightId.length > 0;
    const requireJustification = isWeightIdField
      ? !isFirstWeightIdEntry
      : optionDef?.require_justification_for_change !== false;

    const success = await taskHistory.recordChange({
      fieldPath: `equipment.${config.id}.values.${key}`,
      fieldName: `${config.equipmentType.name}: ${fieldLabel}`,
      oldValue,
      newValue,
      context: `Equipment: ${config.equipmentType.name}`,
      contextType: 'equipment',
      workflowStage: 'configuration',
      requireJustification
    });

    if (success) {
      const updatedConfig = {
        ...config,
        values: { ...config.values, [key]: newValue },
        updated_at: new Date().toISOString()
      };

      updateEquipmentConfiguration(updatedConfig);

      // When weightId is set or changed, backfill all existing weight records
      // that were recorded before the ID was available.
      if (isWeightIdField && newWeightId.length > 0) {
        const records = (activeTask.value as any)?.details?.weightConfiguration?.progressData;
        if (Array.isArray(records)) {
          records.forEach((record: { weightId?: string | null }) => {
            if (!record.weightId || record.weightId.trim().length === 0) {
              record.weightId = newWeightId;
            }
          });
        }
      }
    }
  };

  return {
    // ===================================================================
    // Guards
    // ===================================================================
    isTaskWithEquipment,

    // ===================================================================
    // Getters
    // ===================================================================
    getEquipmentConfigurations,
    getEquipmentTypes,
    getConfiguredEquipmentTypeIds,
    getAvailableEquipmentTypes,
    getEquipmentHistory,

    // ===================================================================
    // Helpers
    // ===================================================================
    formatValue,
    getOptionLabel,
    formatFieldName,
    isOptionModifiable,
    formatTimestamp,
    getInitialValues,
    hasWeightIdRequirement,
    getWeightEquipmentConfig,
    getWeightId,
    isWeightIdConfigured,
    hasMissingWeightIdsInRecords,
    getWeightIdCompletionValidation,

    // ===================================================================
    // Actions
    // ===================================================================
    createEquipmentInConfiguration,
    updateEquipmentConfiguration,
    removeEquipmentConfiguration,
    createNewConfiguration,
    updateConfigValue
  };
}
